import React from "react";
import { MapPin, Clock, Phone, Mail } from "lucide-react";
import { useSite } from "../context/SiteContext";

export default function Visit() {
  const { site } = useSite();
  if (!site) return null;

  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=-73.064%2C41.555%2C-73.044%2C41.570&layer=mapnik&marker=41.5621%2C-73.0539`;
  const dirHref = `https://maps.google.com/?q=${encodeURIComponent(site.map_query || `${site.address_line1} ${site.address_line2}`)}`;

  return (
    <section id="visit" className="relative py-24 md:py-32" data-testid="visit-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="overline text-[#9F5C45] mb-6">06 — Visit our clinic</p>
          <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
            Waterbury’s <em className="italic text-[#3A5A40]">quietest</em> waiting room.
          </h2>
          <p className="text-[#57534E] text-base md:text-lg leading-relaxed mb-10">
            A peaceful Highland Avenue setting with morning light, linen sofas and
            tea always on. Easy on-site parking, with stroller and wheelchair
            access.
          </p>

          <div className="space-y-6">
            <Row icon={MapPin} label="Address" value={<>{site.address_line1}<br />{site.address_line2}</>} />
            <Row icon={Clock} label="Hours" value={<>{site.hours_weekday}<br />{site.hours_weekend}</>} />
            <Row icon={Phone} label="Phone" value={<><span className="text-xs uppercase tracking-[0.22em] text-[#57534E] mr-2">Office</span><a href={`tel:${site.phone}`} className="hover:text-[#3A5A40] transition-colors">{site.phone}</a>{site.phone2 ? <><br /><span className="text-xs uppercase tracking-[0.22em] text-[#57534E] mr-2">Cell</span><a href={`tel:${site.phone2}`} className="hover:text-[#3A5A40] transition-colors">{site.phone2}</a></> : null}</>} />
            <Row icon={Mail} label="Email" value={<a href={`mailto:${site.email}`} className="hover:text-[#3A5A40] transition-colors">{site.email}</a>} />
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <a href={dirHref} target="_blank" rel="noreferrer" data-testid="visit-directions-btn" className="inline-flex items-center gap-2 px-6 py-3 btn-primary text-sm tracking-wide">
              Get directions
            </a>
            <a href="#enroll" data-testid="visit-enroll-btn" className="inline-flex items-center gap-2 px-6 py-3 btn-ghost text-sm tracking-wide">
              Book a first session
            </a>
          </div>
        </div>

        <div className="md:col-span-7 md:col-start-7">
          <div className="aspect-[4/5] md:aspect-[5/6] w-full overflow-hidden border border-[#E2DCD0]">
            <iframe
              title="Let Go Wellness Clinic Location"
              src={mapUrl}
              className="w-full h-full grayscale contrast-95"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex gap-5 border-t border-[#E2DCD0] pt-5">
    <Icon className="w-5 h-5 text-[#3A5A40] flex-shrink-0 mt-1" strokeWidth={1.5} />
    <div>
      <div className="overline text-[#57534E] mb-1">{label}</div>
      <div className="text-[#1C1917] text-base leading-relaxed">{value}</div>
    </div>
  </div>
);
